import { useEffect, useMemo, useState } from "react"
import { Button, Stack } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { Sortable } from "lib/@components/Sortable"
import { RulesPanelRuleSet } from "./rules-panel-rule-set"
import { SCENARIO_CONDITION_FIELDS } from "./scenario-condition-fields"

export function RulesPanelAppliedList({
    ruleSets,
    appliedRuleSetIds,
    onEditRuleSet,
    onRemoveRuleSet,
    onSaveOrder,
    onOrderDirtyChange,
}) {
    const [draftRuleSetIds, setDraftRuleSetIds] = useState(buildInitialDraftRuleSetIds)
    const [savingOrder, setSavingOrder] = useState(false)
    const orderedRuleSets = useMemo(buildOrderedRuleSets, [ruleSets, draftRuleSetIds])
    const orderDirty = !areRuleSetIdsEqual(draftRuleSetIds, appliedRuleSetIds || [])
    const canReorder = Boolean(onSaveOrder && orderedRuleSets.length > 1)
    const appliedRuleSetIdsKey = buildRuleSetIdsKey(appliedRuleSetIds)

    const cancelDisabled = savingOrder
    const saveDisabled = savingOrder

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(syncDraftOrder, [appliedRuleSetIdsKey])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(notifyOrderDirty, [orderDirty])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resetOrderDirtyOnUnmount, [])

    return (
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            <Bound conditionFields={SCENARIO_CONDITION_FIELDS}>
                <Stack spacing={1.5} sx={{ flex: 1, overflow: "auto" }}>
                    <If value={canReorder}>
                        <Sortable items={orderedRuleSets} id="id" onOrdered={handleReorder}>
                            <Repeat
                                list={orderedRuleSets}
                                item={
                                    <RulesPanelRuleSet
                                        canReorder={canReorder}
                                        onEdit={onEditRuleSet}
                                        onRemove={onRemoveRuleSet}
                                        onMoveUp={handleMoveRuleSetUp}
                                        onMoveDown={handleMoveRuleSetDown}
                                    />
                                }
                            />
                        </Sortable>
                        <Else>
                            <Repeat
                                list={orderedRuleSets}
                                item={
                                    <RulesPanelRuleSet
                                        canReorder={canReorder}
                                        onEdit={onEditRuleSet}
                                        onRemove={onRemoveRuleSet}
                                        onMoveUp={handleMoveRuleSetUp}
                                        onMoveDown={handleMoveRuleSetDown}
                                    />
                                }
                            />
                        </Else>
                    </If>
                </Stack>
            </Bound>
            <If value={onSaveOrder && orderDirty}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        data-cy="rules-panel-cancel-order"
                        size="small"
                        onClick={handleCancelOrder}
                        disabled={cancelDisabled}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-cy="rules-panel-save-order"
                        size="small"
                        variant="contained"
                        onClick={handleSaveOrder}
                        disabled={saveDisabled}
                    >
                        Save Order
                    </Button>
                </Stack>
            </If>
        </Stack>
    )

    function buildInitialDraftRuleSetIds() {
        return [...(appliedRuleSetIds || [])]
    }

    function buildOrderedRuleSets() {
        const availableRuleSetsById = new Map((ruleSets || []).map(toRuleSetEntry))
        const ordered = []
        const includedIds = new Set()

        for (const ruleSetId of draftRuleSetIds) {
            const ruleSet = availableRuleSetsById.get(ruleSetId)
            if (!ruleSet) continue
            ordered.push(ruleSet)
            includedIds.add(ruleSetId)
        }

        for (const ruleSet of ruleSets || []) {
            if (!ruleSet?.id || includedIds.has(ruleSet.id)) continue
            ordered.push(ruleSet)
        }

        return ordered
    }

    function syncDraftOrder() {
        setDraftRuleSetIds([...(appliedRuleSetIds || [])])
    }

    function notifyOrderDirty() {
        onOrderDirtyChange?.(orderDirty)
    }

    function resetOrderDirtyOnUnmount() {
        return clearOrderDirty
    }

    function clearOrderDirty() {
        onOrderDirtyChange?.(false)
    }

    function handleReorder(reordered) {
        if (!onSaveOrder) return
        setDraftRuleSetIds(reordered.map(toRuleSetId))
    }

    function handleMoveRuleSetUp(ruleSetId) {
        moveRuleSet(ruleSetId, -1)
    }

    function handleMoveRuleSetDown(ruleSetId) {
        moveRuleSet(ruleSetId, 1)
    }

    function moveRuleSet(ruleSetId, offset) {
        setDraftRuleSetIds((current) => moveRuleSetId(current, ruleSetId, offset))
    }

    function handleCancelOrder() {
        setDraftRuleSetIds([...(appliedRuleSetIds || [])])
    }

    async function handleSaveOrder() {
        if (!orderDirty) return
        setSavingOrder(true)
        try {
            await onSaveOrder?.(draftRuleSetIds)
        } finally {
            setSavingOrder(false)
        }
    }
}

function moveRuleSetId(ruleSetIds, ruleSetId, offset) {
    const currentIndex = ruleSetIds.indexOf(ruleSetId)
    if (currentIndex < 0) return ruleSetIds
    const nextIndex = currentIndex + offset
    if (nextIndex < 0 || nextIndex >= ruleSetIds.length) return ruleSetIds

    const next = [...ruleSetIds]
    const [item] = next.splice(currentIndex, 1)
    next.splice(nextIndex, 0, item)
    return next
}

function toRuleSetEntry(ruleSet) {
    return [ruleSet.id, ruleSet]
}

function toRuleSetId(ruleSet) {
    return ruleSet.id
}

function buildRuleSetIdsKey(ruleSetIds) {
    return (ruleSetIds || []).join("\u0001")
}

function areRuleSetIdsEqual(a, b) {
    if (a.length !== b.length) return false
    return a.every((id, index) => id === b[index])
}
